import React, { KeyboardEvent } from 'react';
import { Hidden, Visible } from 'wix-ui-icons-common';
import { Box } from 'wix-style-react';
import { useTranslation } from 'react-i18next';
import { dataHooks } from '../../dataHooks';

interface WithPasswordVisibilityProps {
  showVisibility: boolean;
}

export function withPasswordVisibility<T extends any>(
  WrappedComponent: React.ComponentType<T>,
) {
  // Try to create a nice displayName for React Dev Tools.
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  // Creating the inner component. The calculated Props type here is the where the magic happens.
  const InputWithPasswordStrength = (
    props: T & WithPasswordVisibilityProps & any,
  ) => {
    const { t } = useTranslation();
    const [visible, toggleVisibility] = React.useReducer(
      (state) => !state,
      false,
    );
    const handleEnterPressed = (e: KeyboardEvent<SVGElement>) => {
      if (e.key === 'Enter') {
        toggleVisibility();
      }
    };
    const showOrHideSuffix = (
      <Box
        verticalAlign="middle"
        dataHook={dataHooks.inputField.visibilityIcon}
      >
        {visible ? (
          <Hidden
            tabIndex={0}
            role="button"
            aria-label={t('hide.password')}
            style={{
              cursor: 'pointer',
            }}
            onClick={toggleVisibility}
            onKeyDown={handleEnterPressed}
          />
        ) : (
          <Visible
            tabIndex={0}
            role="button"
            aria-label={t('show.password')}
            style={{
              cursor: 'pointer',
            }}
            onClick={toggleVisibility}
            onKeyDown={handleEnterPressed}
          />
        )}
      </Box>
    );

    const suffix = [
      ...(props.showVisibility ? [showOrHideSuffix] : []),
      ...(props.suffix ? props.suffix : []),
    ];

    // props comes afterwards so the can override the default ones.
    return (
      <WrappedComponent
        {...(props as T & {})}
        suffix={suffix}
        type={visible ? 'text' : 'password'}
      />
    );
  };

  InputWithPasswordStrength.displayName = `withPasswordStrength(${displayName})`;

  return InputWithPasswordStrength;
}
